import { useNavigate, useParams } from 'react-router-dom'
import { Plus, ChevronRight } from 'lucide-react'
import { useNotes, useCreateNote, useDeleteNote } from '../api/hooks'
import { NoteEditor } from '../components/notes/NoteEditor'
import { useToast } from '../components/primitives/Toast'
import { noteTitle, notePreview, formatNoteTime } from '../components/notes/noteMeta'

/**
 * Mobile notes — a full-screen list that pushes to a full-screen editor, since
 * there is no room for the desktop's side-by-side panel.
 */
export function MobileNotesPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: notes = [], isLoading } = useNotes()
  const createNote = useCreateNote()
  const deleteNote = useDeleteNote()
  const { showToast } = useToast()

  if (isLoading) {
    return <ListMessage>Loading…</ListMessage>
  }

  if (id) {
    const note = notes.find((n) => n.id === id)
    if (!note) return <ListMessage>That note no longer exists.</ListMessage>

    return (
      <div style={{ height: '100%', overflow: 'auto' }}>
        <NoteEditor
          note={note}
          onBack={() => navigate('/notes')}
          onDeleted={() =>
            deleteNote.mutate(note.id, {
              onSuccess: () => navigate('/notes'),
              onError: () => showToast('Failed to delete note', 'error'),
            })
          }
        />
      </div>
    )
  }

  const handleCreate = () =>
    createNote.mutate(
      {},
      {
        onSuccess: (note) => navigate(`/notes/${note.id}`),
        onError: () => showToast('Failed to create note', 'error'),
      }
    )

  return (
    <div style={{ height: '100%', overflow: 'auto', position: 'relative' }}>
      <div style={{ padding: '16px 16px 4px' }}>
        <h1 className="display-serif" style={{ fontSize: '1.75rem', margin: 0 }}>
          Notes
        </h1>
      </div>

      {notes.length === 0 ? (
        <ListMessage>No notes yet. Tap + to start one.</ListMessage>
      ) : (
        <div style={{ padding: '8px 0 96px' }}>
          {notes.map((note) => (
            <button
              key={note.id}
              onClick={() => navigate(`/notes/${note.id}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                padding: '14px 16px',
                background: 'none',
                border: 'none',
                borderBottom: '1px solid var(--color-border)',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontFamily: 'var(--font-serif)',
                    fontSize: '1rem',
                    color: 'var(--color-ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {noteTitle(note)}
                </span>
                <span
                  style={{
                    display: 'block',
                    marginTop: '3px',
                    fontFamily: 'var(--font-serif)',
                    fontSize: '0.8125rem',
                    color: 'var(--color-ink-faint)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatNoteTime(note.updated_at)} · {notePreview(note)}
                </span>
              </span>
              <ChevronRight size={16} strokeWidth={1.5} style={{ color: 'var(--color-ink-faint)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}

      <button
        onClick={handleCreate}
        aria-label="New note"
        style={{
          position: 'absolute',
          right: '18px',
          bottom: '24px',
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'var(--color-accent)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <Plus size={22} strokeWidth={1.5} />
      </button>
    </div>
  )
}

function ListMessage({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        padding: '24px 16px',
        margin: 0,
        fontFamily: 'var(--font-serif)',
        fontStyle: 'italic',
        color: 'var(--color-ink-muted)',
      }}
    >
      {children}
    </p>
  )
}
